/* eslint-disable */
import {defineStore, storeToRefs} from "pinia";
import {computed, reactive, ref} from "vue";
import {coordinator, routes} from "@/plugins/axios";
import {cookieStorage} from "@/pinia/cookieStorage";
import {useTheme} from "@/stores/ThemeStore";
import {useOrganizations} from "@/composables/useOrganizations";
import {useMembers} from "@/composables/useMembers";
import {useNotifications} from "@/composables/useNotifications";
import {useFieldPermissions} from "@/composables/useFieldPermissions";
import {useUsers} from "@/composables/useUsers";
import {getFirebaseTokenOrRequestPermission} from "@/plugins/firebase";
import {useSignUp} from "./SignUpStore";
import {useChat} from "@/composables/useChat";
import moment from "moment";
import {useCheckFeature} from "@/composables/useCheckFeature";
import {useRecaptcha} from "@/composables/useRecaptcha";
import {useProfileBuilder} from "@/stores/ProfileBuilderStore";

const CHAT_TOKEN_STORAGE_KEY = 'chat-token';
const CHAT_TOKEN_TTL_MS = 12 * 60 * 60 * 1000;

export const useAuth = defineStore('useAuth', () => {
    const {getOrganizationBySlug} = useOrganizations();
    const {canViewerRead, canViewerWrite} = useFieldPermissions();
    const {setTheme} = useTheme();
    const {
        getMemberByUserUid,
        getProfileStatus,
        getOnboardingStatus,
        getFieldsStatus,
    } = useMembers();
    const {getNotifications, storeFcmToken} = useNotifications();
    const {inProgress: signUpInProgress} = storeToRefs(useSignUp());
    const {getChatAccessToken} = useChat();
    const {getUser} = useUsers();
    const {getTabs} = useProfileBuilder();
    const {generateRecaptchaToken} = useRecaptcha();

    const loading = ref(false);
    const token = ref(null);
    const notificationToken = ref(null);
    const user = ref(null);
    const member = ref(null);
    const notifications = ref([]);
    const totalNotifications = ref(0);
    const organization = ref(null);
    const errors = reactive({
        phone_number: null,
        code: null,
        password: null
    });

    const isImpersonating = ref(false);
    const impersonationExpiresAt = ref(null);

    const profileStatus = ref(null);
    const onboardingStatus = ref(null);
    const fieldsStatus = ref(null);

    let chatTokenRequest = null;

    const {loadMemberFeatures, clearMemberFeatures} = useCheckFeature();

    const isAuthenticated = computed(() => {
        return token.value !== null;
    });

    const hasMember = computed(() => {
        return member.value !== null;
    });

    const hasToken = computed(() => {
        return token.value !== '';
    });

    const hasOrganization = computed(() => {
        return organization.value !== null;
    });

    const userFullName = computed(() => {
        if (member.value === null) {
            return '';
        }

        return `${member.value.first_name} ${member.value.last_name}`;
    });

    const isAdmin = computed(() => {
        if(!member.value) {
            return false;
        }

        return  member.value.roles.some(
            (role) => role.title === "Organization-admin"
        );
    });

    const isSuperAdmin = computed(() => {
        if(!member.value) {
            return false;
        }

        return  member.value.roles.some(
            (role) => role.title === "Superadmin"
        );
    });

    const isBenefitsAdmin = computed(() => {
        if (!member.value) {
            return false;
        }

        // The combined "Benefits-admin" role was split into health + life; both are benefits-domain
        // admins and hold the profile-pin / alerts permissions.
        return member.value.roles.some(
            (role) => role.title === "Health-benefits-admin" || role.title === "Life-benefits-admin"
        );
    });

    const canPinProfileFields = computed(() => isAdmin.value || isBenefitsAdmin.value);

    const isContentAdmin = computed(() => {
        if(!member.value) {
            return false;
        }

        return member.value.roles.some(
            (role) => role.title === "Content-admin"
        );
    });

    const isRepresentative = computed(() => {
        if(!member.value) {
            return false;
        }

        return member.value.roles.some(
            (role) => role.title === "Representative"
        );
    });

    const isGroupAdmin = computed(() => {
        if (!member.value) {
            return false;
        }

        return member.value.roles.some(
            (role) => role.title === "Group-admin"
        );
    });

    const canManageAlerts = computed(() => isSuperAdmin.value || isAdmin.value || isGroupAdmin.value || isBenefitsAdmin.value);

    const canViewMemberAlerts = computed(
        () =>
            isSuperAdmin.value ||
            isAdmin.value ||
            isBenefitsAdmin.value ||
            isContentAdmin.value ||
            isGroupAdmin.value ||
            isRepresentative.value
    );

    const canExportMembers = computed(() => isSuperAdmin.value || isAdmin.value || isGroupAdmin.value || isBenefitsAdmin.value);

    const canManageMilitaryLeave = computed(() => isSuperAdmin.value || isAdmin.value || isGroupAdmin.value);

    const hasOrganizationFields = computed(() => {
        if (!organization.value?.fields) {
            return false;
        }

        const hasFields = organization.value.fields.some(tab => tab.pb_fields && tab.pb_fields.length > 0)

        if(!hasFields) {
            return false;
        }

        if (organization.value && organization.value.slug && hasFields) {
            return isAdmin.value || organization.value.fields.some(tab => {
                return (tab.pb_fields ?? []).some(field => canViewerRead(field, isAdmin.value));
            });
        }
        return false;
    });

    const hasOrganizationRequiredFields = computed(() => {
        if (!organization.value?.fields) {
            return false;
        }

        const hasFields = organization.value.fields.some(tab => tab.pb_fields && tab.pb_fields.length > 0)

        if (organization.value && organization.value.slug && hasFields) {
            return isAdmin.value || organization.value.fields.some(tab => {
                return (tab.pb_fields ?? []).some(field => field.is_required && canViewerWrite(field, isAdmin.value));
            });
        }
        return false;
    });

    const showCompleteProfileInfoBox = computed(() => {
        return (
            fieldsStatus.value === "member_fields_incomplete" &&
            moment().subtract(30, "days").isBefore(member.value.created_at) &&
            moment(member.value.created_at).isBefore(moment())
        );
    });

    const hasPaymentsActive = computed(() => {
       return organization.value.payments_enabled;
    });

    const hasSplitPaymentsActive = computed(() => {
       return organization.value.payments_enabled && organization.value.split_payments_enabled;
    });

    const hasRecurringInvoicesActive = computed(() => {
       return organization.value.payments_enabled && organization.value.recurring_invoices_enabled;
    });

    const hasLineItemOptionsActive = computed(() => {
       return organization.value.payments_enabled && organization.value.line_item_options_enabled;
    });

    const setOrganizationName = (name) => {
        organization.value.name = name;
    };

    async function getFields(organizationSlug) {
        const response = await getTabs(organizationSlug);

        if (response) {
            organization.value.fields = response.data;
        }
    }

    async function init(organizationSlug) {
        organization.value = {
            slug: organizationSlug
        };

        if (!user.value && cookieStorage.getItem('user-uid') !== 'null') {
            user.value = {
                uid: cookieStorage.getItem('user-uid'),
                phone_number: cookieStorage.getItem('phone_number')
            };
        }

        const memberUserUid = user.value?.uid;
        const loadsMember = memberUserUid && member.value === null && !signUpInProgress.value;

        const [organizationResult] = await Promise.allSettled([
            getOrganizationBySlug(organizationSlug).then((organizationData) => {
                organization.value = {...organizationData.data, fields: organization.value.fields};
                setTheme(organizationData.data.theme_settings);
            }),
            loadsMember && getMemberInfo(organizationSlug, memberUserUid),
        ]);

        if (organizationResult.status === 'rejected') {
            throw organizationResult.reason;
        }

        if (!loadsMember) {
            return;
        }

        getFirebaseTokenOrRequestPermission().then(async (token) => {
            if (token && member.value) {
                const fcmToken = await storeFcmToken(organization.value.slug, member.value.uid, token);
                notificationToken.value = fcmToken.data;
            }
        }).catch((error) => {
            console.warn('Unable to store FCM token.', error);
        });
    }

    async function sendAuthenticationCode(phoneNumber) {
        loading.value = true;

        try {
            const recaptchaToken = await generateRecaptchaToken('login');

            return coordinator.post(routes.auth.sendCode, {
                'phone_number': phoneNumber,
                'recaptcha_token': recaptchaToken
            }).catch(err => {
                errors.phone_number = err.response.data.errors.phone_number;
                return Promise.reject(err);
            }).finally(() => loading.value = false);
        } catch (recaptchaError) {
            loading.value = false;
            errors.phone_number = 'reCAPTCHA verification failed. Please try again.';
            return Promise.reject(recaptchaError);
        }
    }

    async function completeLogin(r) {
        user.value = r.data.user;
        token.value = r.data.token;

        cookieStorage.setItem('user-uid', user.value.uid);

        if (hasOrganization.value) {
            try {
                await getMemberInfo(organization.value.slug, user.value.uid)
            } catch (e) {
                return e.response
            }

            getFirebaseTokenOrRequestPermission().then(async (token) => {
                if (!token || !member.value) return;
                const fcmToken = await storeFcmToken(organization.value.slug, member.value.uid, token);
                notificationToken.value = fcmToken.data;
            }).catch((error) => {
                console.warn('Unable to store FCM token.', error);
            });
        }

        return Promise.resolve(r);
    }

    async function checkAuthenticationCode(phoneNumber, code) {
        loading.value = true;
        return coordinator.post(routes.auth.validateCode, {
            'phone_number': phoneNumber,
            'code': code
        }).then(completeLogin).catch((err) => {
            loading.value = false;
            if (err.response?.status === 400) {
                errors.code = err.response?.data?.message;
            } else {
                errors.code = err.response?.data?.errors;
            }

            return Promise.reject(err);
        });
    }

    async function loginWithPassword(phoneNumber, password) {
        loading.value = true;
        return coordinator.post(routes.auth.passwordLogin, {
            'phone_number': phoneNumber,
            'password': password
        }).then(completeLogin).catch((err) => {
            loading.value = false;
            errors.password = err.response?.data?.message || 'Unable to log in.';

            return Promise.reject(err);
        });
    }

    async function getMemberInfo(organizationSlug, userUid) {
        loading.value = true;
        return getMemberByUserUid(organizationSlug, userUid)
            .then(async (response) => {
                member.value = response.data;

                [profileStatus.value, fieldsStatus.value, onboardingStatus.value] = await Promise.all([
                    getProfileStatus(organizationSlug, member.value.uid).catch((err) => console.error(err)),
                    getFieldsStatus(organizationSlug, member.value.uid).catch((err) => console.error(err)),
                    getOnboardingStatus(organizationSlug, member.value.uid).catch((err) => console.error(err)),
                    getFields(organizationSlug),
                    loadMemberFeatures(organizationSlug).catch((err) => console.error(err)),
                ]);

                return Promise.resolve(true);
            })
            .catch(() => {
                getUser(userUid).catch((err) => {
                    if (err.response?.status === 404) {
                        token.value = null;
                        user.value = null;
                        member.value = null;
                    }
                });
            })
            .finally(() => {
                loading.value = false;
            });
    }

    function ensureChatToken({refresh = false} = {}) {
        if (refresh) {
            clearChatToken();
        }

        const slug = organization.value.slug;
        const owner = `${slug}:${member.value.uid}`;

        if (chatTokenRequest?.owner !== owner) {
            const request = {owner};

            request.promise = resolveChatToken(slug, owner).catch((err) => {
                console.error(err);

                if (chatTokenRequest === request) {
                    chatTokenRequest = null;
                }

                return null;
            });

            chatTokenRequest = request;
        }

        return chatTokenRequest.promise;
    }

    function readCachedChatToken() {
        try {
            return JSON.parse(sessionStorage.getItem(CHAT_TOKEN_STORAGE_KEY));
        } catch {
            return null;
        }
    }

    async function resolveChatToken(slug, owner) {
        const cached = readCachedChatToken();

        if (cached?.owner === owner && cached.expiresAt > Date.now()) {
            return cached.token;
        }

        const {token} = await getChatAccessToken(slug);

        sessionStorage.setItem(CHAT_TOKEN_STORAGE_KEY, JSON.stringify({
            owner,
            token,
            expiresAt: Date.now() + CHAT_TOKEN_TTL_MS,
        }));

        return token;
    }

    function clearChatToken() {
        chatTokenRequest = null;
        sessionStorage.removeItem(CHAT_TOKEN_STORAGE_KEY);
    }

    async function startImpersonation(targetUserUid) {
        const response = await coordinator.post(routes.impersonation.start(targetUserUid));

        token.value = response.data.token;
        user.value = response.data.user;
        member.value = null;
        isImpersonating.value = true;
        impersonationExpiresAt.value = response.data.expires_at;

        cookieStorage.setItem('user-uid', response.data.user.uid);

        clearMemberFeatures();

        await getMemberInfo(organization.value.slug, targetUserUid);
    }

    async function stopImpersonation() {
        try {
            const response = await coordinator.delete(routes.impersonation.stop);

            token.value = response.data.token;
            user.value = response.data.user;
            member.value = null;
            isImpersonating.value = false;
            impersonationExpiresAt.value = null;

            cookieStorage.setItem('user-uid', response.data.user.uid);

            clearMemberFeatures();

            await getMemberInfo(organization.value.slug, response.data.user.uid);
        } catch (e) {
            // Token may be expired — clear auth and redirect to login
            token.value = null;
            user.value = null;
            isImpersonating.value = false;
            impersonationExpiresAt.value = null;
        }
    }

    async function loadNotifications() {
        return getNotifications(organization.value.slug, member.value.uid).then((response) => {
            notifications.value = response.data ?? [];
            totalNotifications.value = response.meta?.total_unread ?? 0;

            return Promise.resolve(response);
        });
    }

    return {
        loading,
        token,
        notificationToken,
        user,
        member,
        notifications,
        organization,
        errors,
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        isBenefitsAdmin,
        isContentAdmin,
        isRepresentative,
        isGroupAdmin,
        canPinProfileFields,
        canManageAlerts,
        canViewMemberAlerts,
        canExportMembers,
        canManageMilitaryLeave,
        hasToken,
        hasMember,
        hasOrganization,
        userFullName,
        init,
        getMemberInfo,
        sendAuthenticationCode,
        checkAuthenticationCode,
        loginWithPassword,
        getOrganizationBySlug,
        getNotifications,
        hasOrganizationFields,
        hasOrganizationRequiredFields,
        profileStatus,
        fieldsStatus,
        onboardingStatus,
        loadNotifications,
        totalNotifications,
        setOrganizationName,
        showCompleteProfileInfoBox,
        hasPaymentsActive,
        hasSplitPaymentsActive,
        hasRecurringInvoicesActive,
        hasLineItemOptionsActive,
        isImpersonating,
        impersonationExpiresAt,
        startImpersonation,
        stopImpersonation,
        ensureChatToken,
        clearChatToken,
    };
}, {
    persist: [
        {
            key: 'auth',
            storage: cookieStorage,
            paths: ['token', 'isImpersonating', 'impersonationExpiresAt'],
            debug: true,
        },
    ]
});
